import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { careService } from "../services/careService";

export const defaultSiteProfile = {
  name: "",
  themePhrase: "",
  phone: "",
  email: "",
  location: "",
  logo: "",
  logoUrl: "",
  favicon: "",
  faviconUrl: "",
  mainColor: "",
  secondaryColor: "",
  siteThemeColor: "",
  secondarySiteThemeColor: "",
};

const SiteProfileContext = createContext(null);
let cachedSiteProfile = null;
let cachedSiteProfilePromise = null;

const normalizeSiteProfile = (profile = {}) => ({
  ...defaultSiteProfile,
  ...(profile || {}),
});

const displayableUrl = (value) => {
  const url = String(value || "").trim();
  return /^(https?:|\/|data:image|blob:)/i.test(url) ? url : "";
};

const setFaviconLink = (href) => {
  if (typeof document === "undefined" || !href) return;
  let icon = document.head.querySelector('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.appendChild(icon);
  }
  icon.href = href;
};

const fetchGuestSiteProfile = async ({ force = false } = {}) => {
  if (!force && cachedSiteProfile) return cachedSiteProfile;
  if (!force && cachedSiteProfilePromise) return cachedSiteProfilePromise;

  cachedSiteProfilePromise = careService.siteProfile
    .getGuest()
    .then((profile) => {
      cachedSiteProfile = normalizeSiteProfile(profile);
      return cachedSiteProfile;
    })
    .finally(() => {
      cachedSiteProfilePromise = null;
    });

  return cachedSiteProfilePromise;
};

export function SiteProfileProvider({ children }) {
  const [siteProfile, setSiteProfile] = useState(cachedSiteProfile || defaultSiteProfile);
  const [loading, setLoading] = useState(!cachedSiteProfile);
  const [error, setError] = useState("");

  const loadSiteProfile = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const profile = await fetchGuestSiteProfile({ force });
      setSiteProfile(profile);
      return profile;
    } catch (nextError) {
      setSiteProfile(defaultSiteProfile);
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load site profile.");
      return defaultSiteProfile;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cachedSiteProfile) {
      setSiteProfile(cachedSiteProfile);
      setLoading(false);
      return;
    }
    loadSiteProfile();
  }, [loadSiteProfile]);

  useEffect(() => {
    const faviconUrl = displayableUrl(siteProfile.faviconUrl)
      || displayableUrl(siteProfile.faviconSignedUrl)
      || displayableUrl(siteProfile.favicon);
    setFaviconLink(faviconUrl);
  }, [siteProfile.favicon, siteProfile.faviconSignedUrl, siteProfile.faviconUrl]);

  const value = useMemo(
    () => ({
      error,
      loading,
      refreshSiteProfile: () => loadSiteProfile({ force: true }),
      siteProfile,
    }),
    [error, loadSiteProfile, loading, siteProfile],
  );

  return (
    <SiteProfileContext.Provider value={value}>
      {children}
    </SiteProfileContext.Provider>
  );
}

export function useSiteProfileContext() {
  const context = useContext(SiteProfileContext);
  if (!context) {
    return {
      error: "",
      loading: false,
      refreshSiteProfile: async () => defaultSiteProfile,
      siteProfile: defaultSiteProfile,
    };
  }
  return context;
}
